'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Minus, Plus, Search } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useLayoutEffect, useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { defaultFAQs, faqCategories } from '@/lib/constants/faq-data';

// New interfaces
export interface FAQ {
	question: string;
	answer: string;
	category: string;
}

interface FAQSectionProps {
	heading?: string;
	subheading?: string;
	FAQs?: FAQ[];
	isFirst?: boolean;
}

const Answer = ({ content }: { content: string }) => (
	<div dangerouslySetInnerHTML={{ __html: content }} />
);

const faqVisibilityStyles = `
	.faq-section .faq-static-fallback {
		display: block;
	}
	.faq-section .faq-interactive {
		display: none;
	}
	html.js .faq-section .faq-static-fallback {
		display: none;
	}
	html.js .faq-section .faq-interactive {
		display: block;
	}
`;

export function FAQSection({
	heading = 'Frequently Asked Questions',
	subheading = "Everything you need to know about our services. Can't find the answer you're looking for?",
	FAQs = defaultFAQs,
	isFirst = false,
}: FAQSectionProps) {
	const SectionHeading = sectionHeadingTag(isFirst);
	// Get unique categories from FAQs or use default categories
	const categories = faqCategories.filter(cat =>
		FAQs.some(faq => faq.category === cat.name),
	);
	const defaultCategory = categories[0]?.name ?? 'General';

	const [isEnhanced, setIsEnhanced] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeCategory, setActiveCategory] = useState(defaultCategory);
	const [openItems, setOpenItems] = useState<string[]>([
		`${defaultCategory}-0`,
	]);
	const toggleItem = (questionId: string) => {
		setOpenItems(prev =>
			prev.includes(questionId)
				? prev.filter(id => id !== questionId)
				: [...prev, questionId],
		);
	};

	const filteredFAQs = FAQs.filter(
		faq =>
			faq.category === activeCategory &&
			(faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
				faq.answer.toLowerCase().includes(searchTerm.toLowerCase())),
	);
	useLayoutEffect(() => {
		document.documentElement.classList.add('js');
		setIsEnhanced(true);
	}, []);

	useEffect(() => {
		const firstQuestionId = `${activeCategory}-0`;
		setOpenItems([firstQuestionId]);
	}, [activeCategory]);

	const headingBlock = (
		<div className="mx-auto mb-4 max-w-3xl text-center">
			<SectionHeading className="mb-6 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-600 bg-clip-text text-4xl font-bold tracking-tight text-transparent">
				{heading}
			</SectionHeading>
			<p className="mx-auto max-w-2xl text-lg text-gray-600">
				{subheading}{' '}
				<Link
					href="/contact-us"
					className="inline-flex items-center gap-1 p-0 font-semibold text-blue-600 hover:text-blue-700">
					Contact our support team
					<ChevronRight className="h-4 w-4" />
				</Link>
			</p>
		</div>
	);

	return (
		<section className="faq-section relative overflow-hidden bg-gradient-to-b from-gray-50 via-white to-gray-50/20 py-8">
			<style>{faqVisibilityStyles}</style>
			<div className="container relative mx-auto px-4">
				<div className="mx-auto mb-4 max-w-4xl">
					{isEnhanced ? (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}>
							{headingBlock}
						</motion.div>
					) : (
						headingBlock
					)}
				</div>
				<div
					className="faq-static-fallback mx-auto max-w-4xl"
					hidden={isEnhanced}
					aria-hidden={isEnhanced}>
					{categories.map(category => (
						<div key={category.name} className="mb-8">
							<h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-gray-900">
								<span>{category.icon}</span>
								{category.name}
							</h3>
							<div className="space-y-4 rounded-2xl border border-gray-100 bg-white/90 p-6 shadow-xl">
								{FAQs.filter(
									faq => faq.category === category.name,
								).map((faq, index) => (
									<div
										key={`${category.name}-static-${index}`}
										className="rounded-xl border border-gray-200 bg-gray-50/80 p-6">
										<h4 className="mb-3 text-lg font-medium text-gray-900">
											{faq.question}
										</h4>
										<div className="prose prose-blue prose-p:leading-relaxed max-w-none text-gray-600">
											<Answer content={faq.answer} />
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				<div
					className="faq-interactive mx-auto max-w-4xl"
					data-testid="faq-interactive">
					<div className="mb-6">
						<div className="group relative">
							<Search
								className="absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400 transition-colors group-focus-within:text-blue-500"
								size={20}
							/>
							<Input
								type="text"
								placeholder="Search FAQs..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="rounded-2xl border-gray-200 py-6 pl-12 text-lg shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
							/>
						</div>
					</div>

					<div className="mb-6 flex flex-wrap gap-3">
						{categories.map(category => (
							<Button
								key={category.name}
								onClick={() => setActiveCategory(category.name)}
								variant={
									activeCategory === category.name
										? 'default'
										: 'outline'
								}
								className={`gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 ${
									activeCategory === category.name
										? 'bg-blue-600 text-white shadow-md shadow-blue-500/10 hover:bg-blue-700'
										: 'border-gray-200 hover:bg-gray-100'
								}`}>
								<span className="text-base">
									{category.icon}
								</span>
								{category.name}
								<Badge
									variant="secondary"
									className={`ml-1 ${activeCategory === category.name ? 'bg-blue-500/20 text-white' : 'bg-gray-100'}`}>
									{
										FAQs.filter(
											faq =>
												faq.category === category.name,
										).length
									}
								</Badge>
							</Button>
						))}
					</div>

					<Card className="rounded-2xl border-none bg-white/90 shadow-xl backdrop-blur-sm">
						<CardContent className="space-y-4 p-6">
							<AnimatePresence mode="wait">
								{filteredFAQs?.map((item, index) => {
									const questionId = `${activeCategory}-${index}`;
									const isOpen =
										openItems.includes(questionId);

									return (
										<div
											key={questionId}
											className={`rounded-xl border transition-all duration-300 ${
												isOpen
													? 'border-gray-200 bg-gray-50/80 shadow-sm'
													: 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'
											}`}>
											<button
												onClick={() =>
													toggleItem(questionId)
												}
												className="group flex w-full items-center justify-between px-6 py-5 text-left">
												<span
													className={`text-lg font-medium transition-colors duration-200 ${
														isOpen
															? 'text-blue-600'
															: 'text-gray-900 group-hover:text-blue-600'
													}`}>
													{item.question}
												</span>
												{isOpen ? (
													<Minus className="h-5 w-5 text-blue-600 transition-transform duration-200" />
												) : (
													<Plus className="h-5 w-5 text-gray-400 transition-transform duration-200 group-hover:text-blue-600" />
												)}
											</button>
											<AnimatePresence>
												{isOpen && (
													<motion.div
														initial={{
															height: 0,
															opacity: 0,
														}}
														animate={{
															height: 'auto',
															opacity: 1,
														}}
														exit={{
															height: 0,
															opacity: 0,
														}}
														transition={{
															duration: 0.2,
														}}
														className="overflow-hidden">
														<div className="prose prose-blue prose-p:leading-relaxed max-w-none px-6 pb-6 text-gray-600">
															<Answer
																content={
																	item.answer
																}
															/>
														</div>
													</motion.div>
												)}
											</AnimatePresence>
										</div>
									);
								})}
							</AnimatePresence>

							{filteredFAQs?.length === 0 && (
								<div className="px-4 py-8 text-center">
									<div className="mb-4 text-gray-400">
										<Search className="mx-auto h-12 w-12" />
									</div>
									<p className="mb-2 text-xl font-medium text-gray-900">
										No FAQs found
									</p>
									<p className="text-gray-500">
										Try different keywords or{' '}
										<Link href="/contact-us">
											<Button
												variant="link"
												className="inline-flex items-center gap-1 p-0 font-medium text-blue-600 hover:text-blue-700">
												contact support
												<ChevronRight className="h-4 w-4" />
											</Button>
										</Link>
									</p>
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		</section>
	);
}
